import { useMemo, useState } from "react";
import { ANNA, TRAINER_META } from "../content/catalog";
import { displayPeople, forecast } from "../domain/calculator";
import { TrainerIcon, trainerIconClass } from "./shell";

export const ANNA_SITE = "https://anna-clever.clever.party1207.online";

const PRESET = {
  views: "50000",
  ctr: "3",
  trial: "20",
  paid: "12",
  check: "2490",
};

function asNumber(value: string, fallback: number): number {
  const n = Number(value.replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

function money(kopecks: bigint): string {
  return `${(Number(kopecks) / 100).toLocaleString("ru-RU")} ₽`;
}

/** Purely decorative — two soft washes behind the hero copy. Not a real chart, no data. */
function HeroWash() {
  return (
    <svg className="sell-hero-wash" viewBox="0 0 640 420" aria-hidden="true" focusable="false">
      <circle cx="560" cy="30" r="180" fill="#f3edff" />
      <circle cx="20" cy="380" r="150" fill="#fff2e8" />
      <circle cx="330" cy="440" r="120" fill="#e8f6f1" />
    </svg>
  );
}

function TrainerBadge({ slug }: { slug: string }) {
  return (
    <span className={`icon-badge ${trainerIconClass(slug)}`} aria-hidden="true">
      <TrainerIcon slug={slug} />
    </span>
  );
}

export function SellingCalculator() {
  const [views, setViews] = useState(PRESET.views);
  const [ctr, setCtr] = useState(PRESET.ctr);
  const [trial, setTrial] = useState(PRESET.trial);
  const [paid, setPaid] = useState(PRESET.paid);
  const [check, setCheck] = useState(PRESET.check);
  const result = useMemo(
    () =>
      forecast({
        model: "one_time",
        views: asNumber(views, Number(PRESET.views)),
        ctr: asNumber(ctr, Number(PRESET.ctr)),
        visitToTrial: asNumber(trial, Number(PRESET.trial)),
        trialToPaid: asNumber(paid, Number(PRESET.paid)),
        averageCheckKopecks: BigInt(Math.round(asNumber(check, Number(PRESET.check)) * 100)),
        monthlyChurn: 0,
        horizonMonths: 3,
      }),
    [views, ctr, trial, paid, check],
  );
  const month = result.months[0]!;
  const steps = [
    { value: displayPeople(asNumber(views, Number(PRESET.views))).toLocaleString("ru-RU"), label: "охват Reels", tone: "a" },
    { value: displayPeople(month.visits).toLocaleString("ru-RU"), label: "переходы на сайт", tone: "b" },
    { value: displayPeople(month.newBuyers).toLocaleString("ru-RU"), label: "оплаты за месяц", tone: "c" },
  ];
  return (
    <section className="sell-calc" id="calc">
      <div className="sell-calc-copy">
        <p className="badge">Калькулятор</p>
        <h2>Посчитайте по своим числам</h2>
        <p className="lead">Это ваша модель, а не статистика Clever: калькулятор перемножает проценты, которые вы поставили. Меняйте любую строку — сумма пересчитается сразу.</p>
        <div className="calc-fields">
          <label className="field">
            Охват Reels за месяц
            <input inputMode="numeric" value={views} onChange={(e) => setViews(e.target.value)} />
          </label>
          <label className="field">
            Переходы на сайт, %
            <input inputMode="decimal" value={ctr} onChange={(e) => setCtr(e.target.value)} />
          </label>
          <label className="field">
            Начинают пробный доступ, %
            <input inputMode="decimal" value={trial} onChange={(e) => setTrial(e.target.value)} />
          </label>
          <label className="field">
            Оплачивают после пробного, %
            <input inputMode="decimal" value={paid} onChange={(e) => setPaid(e.target.value)} />
          </label>
          <label className="field field-wide">
            Средний чек, ₽
            <input inputMode="numeric" value={check} onChange={(e) => setCheck(e.target.value)} />
          </label>
        </div>
        <p className="muted calc-note">Пока идёт beta, оплата тестовая: деньги не списываются и не выплачиваются.</p>
      </div>
      <div className="sell-funnel">
        <p className="sell-money-label">вам в месяц, после комиссии Clever 20%</p>
        <p className="sell-money">{money(month.sellerNet)}</p>
        <ol className="funnel-steps">
          {steps.map((s, i) => (
            <li key={s.label} className={`funnel-step tone-${s.tone}`}>
              {i > 0 ? <span className="funnel-arrow" aria-hidden="true">→</span> : null}
              <span className="funnel-step-body">
                <strong>{s.value}</strong>
                <span>{s.label}</span>
              </span>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function TutorLanding() {
  return (
    <main className="page sell">
      <section className="sell-hero">
        <HeroWash />
        <div className="sell-copy">
          <p className="badge">Clever для репетиторов</p>
          <h1>Тренажёры английского под вашим именем — на вашем адресе</h1>
          <p className="lead">
            Дети занимаются сами: 8–15 минут, без вашего участия и без школьных оценок. Вам остаётся 80% с каждой оплаты, остальное берёт Clever.
          </p>
          <div className="row">
            <a className="btn" href="/register">Открыть свой сайт</a>
            <a className="btn ghost" href={ANNA_SITE}>Посмотреть сайт Анны</a>
          </div>
          <p className="kicker">{ANNA_SITE.replace("https://", "")}</p>
        </div>
        <aside className="sell-preview card">
          <div className="browser-chrome" aria-hidden="true">
            <span className="chrome-dot" />
            <span className="chrome-dot" />
            <span className="chrome-dot" />
            <span className="chrome-url">{ANNA_SITE.replace("https://", "")}</span>
          </div>
          <p className="badge">Пример сайта</p>
          <p className="sell-preview-name">{ANNA.name}</p>
          <p className="muted">{ANNA.specialization}</p>
          <ul className="tiles">
            {TRAINER_META.map((t) => (
              <li key={t.slug}>
                <a href={ANNA_SITE}>
                  <TrainerBadge slug={t.slug} />
                  <strong>{t.title}</strong>
                  <em>{t.goal}</em>
                </a>
              </li>
            ))}
          </ul>
        </aside>
      </section>

      <section className="sell-trainers">
        <h2>Как выглядит одно занятие</h2>
        <p className="lead">Три тренажёра — три шага: услышать звук, узнать слово, собрать фразу.</p>
        <div className="peek-grid">
          <article className="peek-card tone-a">
            <p className="peek-label"><TrainerBadge slug="english-sounds" />Английские звуки</p>
            <div className="ipa-lg mini" aria-hidden="true">/f/</div>
            <p className="peek-caption">Ребёнок слышит звук и находит его в словах.</p>
          </article>
          <article className="peek-card tone-b">
            <p className="peek-label"><TrainerBadge slug="word-in-frame" />Слово в кадре</p>
            <div className="word mini" aria-hidden="true">
              <span>🪟</span>
              <strong>window</strong>
              <em>окно</em>
            </div>
            <p className="peek-caption">Ребёнок слышит реплику и выкладывает по ней сцену.</p>
          </article>
          <article className="peek-card tone-c">
            <p className="peek-label"><TrainerBadge slug="phrase-director" />Фразовый режиссёр</p>
            <div className="slots mini" aria-hidden="true">
              <span className="slot filled">I</span>
              <span className="slot filled">see</span>
              <span className="slot filled">a</span>
              <span className="slot filled">cat</span>
            </div>
            <p className="peek-caption">Ребёнок собирает фразу из слов и проговаривает её.</p>
          </article>
        </div>
        <div className="peek-footer">
          <p className="muted">Демо-занятия открыты без регистрации — пройдите их как ученик.</p>
          <a className="btn ghost" href="/preview/english-sounds">Пройти демо-занятие</a>
        </div>
      </section>

      <section className="sell-build card">
        <h2>Нужен тренажёр под вашу методику — соберём</h2>
        <p>
          Опишите, чему хотите научить: свои слова, свой порядок, свои примеры. Мы собираем занятие и ставим его на ваш адрес.
        </p>
        <a className="btn" href="/register">Заказать тренажёр</a>
      </section>

      <SellingCalculator />
    </main>
  );
}
